import { SocialMediaLinks } from "./types"

export const emptyUser: User = {
  id: "",
  privyId: "",
  createdAt: Date.now(),
  displayName: {
    name: "",
    isTruncated: false,
    isPrivate: undefined,
    fullName: "",
  },
}

/** Public user fields that exist in a User record in database */
export interface PublicUserInternal extends UserAuthorDetails {
  /** User ID */
  id: string
}

/** All user fields (public and private) that exist in a User record in the DB */
export interface UserInternal extends PublicUserInternal {
  /** User Privy ID */
  privyId: string

  /** Created at timestamp */
  createdAt: number

  /** Whether the user is unsubscribed from promotional emails */
  unsubscribed?: boolean

  /**
   * If the user has been disabled (ie, due to abuse).
   * Note that if the user is disabled, we also set all their posts to 'ugc': 'false", so that
   * they are sufficiently excluded from the 'discover' page.
   */
  disabled?: boolean

  /**
   * The wallet address for the creator referrer, that referred this user to Paragraph.
   * This should ALWAYS be lowercase.
   */
  referrerWalletAddress?: string

  /** The email address of the user */
  email?: string

  /** The primary wallet address of the user */
  wallet_address?: `0x${string}`

  /** The primary wallet address of the user (lowercased) */
  lowercase_wallet_address?: `0x${string}`

  /** All the user's wallets */
  wallets?: WalletAccount[]
}

/** Public user fields in user response */
export interface PublicUser
  extends PublicUserInternal,
    UserAuthorWalletAddress {
  /** User display name (see getUserDisplayName) */
  displayName: UserDisplayName
}

/** All user fields (public and private) in user response */
export interface User extends UserInternal, PublicUser {
  //** User email */
  email?: string
}

export type UserAuthorWalletAddress = {
  /** User wallet address */
  wallet_address?: `0x${string}`

  /** User wallet address (lowercased) */
  lowercase_wallet_address?: `0x${string}`

  /** If Privy embedded wallet */
  isEmbeddedWallet?: boolean
}

/** User display name (See getUserDisplayName) */
export type UserDisplayName = {
  /** User display name (possibly truncated) */
  name: string

  /** If the name is truncated */
  isTruncated: boolean

  /** If the name and fullname are private (e.g. email) */
  isPrivate?: boolean

  /** The untruncated display name */
  fullName: string
}

/** User fields specific to their author info */
export interface UserAuthorDetails {
  /** Author's name */
  authorName?: string

  /** One sentence or paragraph in which the author describes themselves */
  authorBio?: string

  /** User's avatar/profile picture */
  avatar_url?: string

  /** Social media links */
  social?: SocialMediaLinks
}

/** Privy custom metadata object */
export type PrivyCustomMetadata = {
  /** The Privy user's corresponding Firestore User ID */
  userId: string
  /**
   *  If the user is disabled
   *  Corresponds to the disabled flag on the user record and is included
   *  in the custom metadata so we can check the disabled state of a user
   *  during the token verification flow
   **/
  disabled?: boolean
}

/** Farcaster account */
export type FarcasterAccount = {
  type: "farcaster"
  /** Farcaster ID */
  fid: number
  /** Farcaster username */
  username: string
  /** Farcaster custody address (not lowercased) */
  address: `0x${string}`
  /** Updated at timestamp */
  updatedAt: number
}

/** Wallet account */
export type WalletAccount = {
  type: "wallet"
  /** Wallet address (not lowercased) */
  address: `0x${string}`
  /** Wallet client (e.g. privy (embedded wallet), metamask, rainbow, etc). Wallets linked through Farcaster won't have this defined */
  clientType?: string
  /** Updated at timestamp */
  updatedAt: number
}

/** Email account */
export type EmailAccount = {
  type: "email"
  /** Email address (lowercased) */
  email: string
  /** Updated at timestamp */
  updatedAt: number
}

/** Linked account data */
export type Account = EmailAccount | WalletAccount | FarcasterAccount

/** User data for logging */
export type UserLogData = {
  id: string
  email?: string
  wallet_address?: `0x${string}`
  lowercase_wallet_address?: `0x${string}`
  disabled?: boolean
}

/**
 * A shallow user that does not have a Privy account.
 * This is used for users who subscribed (imported or explicitly) but
 * have not actually logged into the platform
 **/
export type ShallowUser = {
  id: string
  email?: string
  wallet_address?: `0x${string}`
  lowercase_wallet_address?: `0x${string}`
  createdAt: number
}
